import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Briefcase, CalendarDays, Receipt, Users } from "lucide-react";
import { api } from "@/lib/api";
import { ORDER_STEPS } from "@/lib/format";
import { Btn, Chip, Empty, PageHeader, Pill, ProgressBar, Spinner } from "@/components/rp";
import { StaffDialog } from "@/components/admin/pratica";
import { PhaseMapEditor } from "@/components/admin/phaseMap";
import { ADMIN_LABELS, Dot, Legend } from "@/components/admin/labels";

const AdminMatrix = ({ list }) => (
  <div className="space-y-3 mb-6">
    <Legend />
    <div className="rp-card overflow-x-auto" data-testid="admin-matrix">
      <table className="w-full text-sm">
        <thead><tr>
          <th className="p-3 rp-label text-left sticky left-0 bg-white min-w-[220px]">Cantiere</th>
          {Object.entries(ADMIN_LABELS).map(([k, l]) => <th key={k} className="p-2 text-[10px] font-semibold text-[#757575] align-bottom min-w-[54px]"><span className="block [writing-mode:vertical-rl] rotate-180 max-h-48 mx-auto">{l}</span></th>)}
        </tr></thead>
        <tbody>
          {list.map((x) => (
            <tr key={x.order_id} className="border-t border-[#EBEBEB]" data-testid={`admin-matrix-row-${x.order_id}`}>
              <td className="p-3 sticky left-0 bg-white"><Link to={`/admin/commesse/${x.order_id}`} className="font-medium hover:underline">{x.title}</Link><p className="text-xs text-[#757575]">{x.code} · {x.client_name} · {x.client_progress.done}/{x.client_progress.total}</p></td>
              {Object.keys(ADMIN_LABELS).map((k) => <td key={k} className="p-2 text-center"><Dot s={x.client_status?.[k]} testid={`admin-dot-${x.order_id}-${k}`} /></td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const STEP = Object.fromEntries(ORDER_STEPS);

const Row = ({ x }) => (
  <Link to={`/admin/amministrazione/${x.order_id}`} className="rp-card p-4 sm:px-6 flex flex-wrap items-center gap-4 hover:border-[#C8BDAB] transition-colors" data-testid={`pratica-row-${x.order_id}`}>
    <div className="flex-1 min-w-[220px]">
      <p className="text-xs font-mono text-[#757575]">{x.code}</p>
      <p className="font-medium">{x.title}</p>
      <p className="text-sm text-[#757575]">{x.client_name} · {STEP[x.status] || x.status}</p>
    </div>
    <div className="w-44">
      <p className="text-xs text-[#757575] mb-1">Pratica cliente {x.client_progress.done}/{x.client_progress.total}</p>
      <ProgressBar value={x.client_progress.total ? (x.client_progress.done / x.client_progress.total) * 100 : 0} />
    </div>
    <div className="flex flex-wrap gap-1 justify-end">
      <Pill tone="muted">{x.parties} fornitori</Pill>
      {x.subcontract_needed === null && <Pill tone="warning">Subappalto da decidere</Pill>}
      {x.mine > 0 && <Pill tone="sand">{x.mine} mie</Pill>}
      {x.overdue > 0 && <Pill tone="error">{x.overdue} scadute</Pill>}
      <Pill tone={x.pending ? "warning" : "success"}>{x.pending} da completare</Pill>
    </div>
  </Link>
);

export default function AdminAmministrazione() {
  const [mine, setMine] = useState(false);
  const [list, setList] = useState(null);
  const [staff, setStaff] = useState([]);
  const [dialog, setDialog] = useState(false);
  const [rules, setRules] = useState(false);
  const [view, setView] = useState("lista");
  const loadStaff = () => api.get("/admin/staff").then((r) => setStaff(r.data));
  useEffect(() => { loadStaff(); }, []);
  useEffect(() => { setList(null); api.get("/amministrazione", { params: { mine } }).then((r) => setList(r.data)); }, [mine]);
  return (
    <div data-testid="admin-amministrazione-page">
      <PageHeader label="Area amministrativa" title="Amministrazione">
        <Link to="/admin/amministrazione/scadenzario" className="rp-btn bg-[#070707] text-white hover:bg-black/80" data-testid="scadenzario-open-button"><CalendarDays className="h-4 w-4" /> Scadenzario</Link>
        <Btn variant="outline" onClick={() => setRules(!rules)} data-testid="phase-map-open-button"><Receipt className="h-4 w-4" /> Fattura a ogni fase</Btn>
        <Btn variant="outline" onClick={() => setDialog(true)} data-testid="staff-open-button"><Users className="h-4 w-4" /> Staff ({staff.length})</Btn>
      </PageHeader>
      <p className="text-sm text-[#757575] -mt-5 mb-6">Pratiche di ogni cantiere: documenti e fatture del cliente, subappaltatori e collaboratori.</p>
      {rules && <div className="mb-6"><PhaseMapEditor url="/amministrazione/settings/phase-invoices" /></div>}
      <div className="flex gap-2 mb-5">
        <Chip active={!mine} onClick={() => setMine(false)} data-testid="pratiche-filter-all">Tutti i cantieri</Chip>
        <Chip active={mine} onClick={() => setMine(true)} data-testid="pratiche-filter-mine">Le mie voci</Chip>
        <span className="w-px bg-[#EBEBEB] mx-1" />
        <Chip active={view === "lista"} onClick={() => setView("lista")} data-testid="pratiche-view-list">Elenco</Chip>
        <Chip active={view === "timeline"} onClick={() => setView("timeline")} data-testid="pratiche-view-timeline">Timeline di tutti i cantieri</Chip>
      </div>
      {list && list.length > 0 && view === "timeline" && <AdminMatrix list={list} />}
      {view === "lista" && (!list ? <Spinner /> : !list.length ? <Empty icon={Briefcase} title="Nessuna pratica" text={mine ? "Non hai voci assegnate da completare." : "Le pratiche si aprono con i cantieri."} data-testid="pratiche-empty" /> : (
        <div className="space-y-2" data-testid="pratiche-list">{list.map((x) => <Row key={x.order_id} x={x} />)}</div>
      ))}
      <StaffDialog open={dialog} onClose={() => setDialog(false)} staff={staff} onCreated={loadStaff} />
    </div>
  );
}
